import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/components/AuthProvider';
import { Role, FeatureAccess, RolePermissions } from '@/types/firebase';
import { createRole, updateRole } from '@/lib/api';
import { showNotification } from '@/lib/generalNotifications';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AlertCircle } from 'lucide-react';

interface RoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  role?: Role | null;
  onSuccess: () => void;
}

// Default permissions for new custom roles
const defaultPermissions: RolePermissions = {
  dashboard: true, // Always enable dashboard access for all roles
  invoices: {
    canView: true,
    canCreate: true,
    canEdit: true,
    canDelete: true
  },
  customers: {
    canView: true,
    canCreate: true,
    canEdit: true,
    canDelete: true
  },
  products: {
    canView: true,
    canCreate: true,
    canEdit: true,
    canDelete: true
  },
  transactions: {
    canView: true,
    canCreate: true,
    canEdit: true,
    canDelete: true
  },
  users: {
    canView: false,
    canCreate: false,
    canEdit: false,
    canDelete: false
  },
  settings: false,
  virtualTerminal: false, // Requires explicit permission
  donations: {
    canView: false,
    canCreate: false,
    canEdit: false,
    canDelete: false
  }
};

export function RoleDialog({ open, onOpenChange, role, onSuccess }: RoleDialogProps) {
  const { user: currentUser } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [permissions, setPermissions] = useState<RolePermissions>(defaultPermissions);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState<string>('general');

  const isEditMode = !!role;
  const isAdminRole = role?.name?.toLowerCase() === 'admin' || role?.name?.toLowerCase() === 'administrator';
  const isSuperAdminRole = role?.name?.toLowerCase() === 'super_admin';

  useEffect(() => {
    if (role) {
      setName(role.name || '');
      setDescription(role.description || '');
      setPermissions(role.permissions || defaultPermissions);
    } else {
      // Default values for new role
      setName('');
      setDescription('');
      setPermissions(defaultPermissions);
    }
    
    // Reset to general tab when dialog opens
    setActiveTab('general');
  }, [role, open]);

  // If this is an Admin or Super Admin role, display a notice and close the dialog
  useEffect(() => {
    if (open && (isAdminRole || isSuperAdminRole)) {
      showNotification({
        title: `${isAdminRole ? 'Admin' : 'Super Admin'} Role`,
        message: `The ${isAdminRole ? 'Admin' : 'Super Admin'} role cannot be modified as it requires specific permissions.`,
        type: 'warning'
      });
      onOpenChange(false);
    }
  }, [open, isAdminRole, isSuperAdminRole, onOpenChange]);

  const validate = () => {
    const newErrors: Record<string, string> = {};
    
    if (!name.trim()) {
      newErrors.name = 'Role name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Menu-item only permission change handler
  const handleMenuItemAccess = (feature: keyof RolePermissions, checked: boolean) => {
    if (feature === 'dashboard' || feature === 'settings' || feature === 'virtualTerminal') {
      // For simple boolean permissions
      setPermissions(prev => ({
        ...prev,
        [feature]: checked
      }));
    } else {
      // For feature permissions, we only care about the canView property
      // which controls menu item visibility. When enabled, we also enable all other permissions
      // (canCreate, canEdit, canDelete) to grant full permissions when the menu is accessible.
      setPermissions(prev => ({
        ...prev,
        [feature]: {
          canView: checked,
          canCreate: checked,
          canEdit: checked,
          canDelete: checked
        }
      }));
    }
  };

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }
    
    if (!currentUser?.companyId || !currentUser?.uid) {
      return;
    }
    
    // Additional check to prevent updating Admin or Super Admin roles
    if (isEditMode && (isAdminRole || isSuperAdminRole)) {
      showNotification({
        title: 'Error',
        message: `The ${isAdminRole ? 'Admin' : 'Super Admin'} role cannot be modified.`,
        type: 'error'
      });
      onOpenChange(false);
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      if (isEditMode && role) {
        // Update existing role
        await updateRole(role.id, {
          name,
          description,
          permissions,
          updatedAt: new Date()
        });
        
        showNotification({
          title: 'Success',
          message: 'Role updated successfully',
          type: 'success'
        });
      } else {
        // Create new role
        await createRole({
          name,
          description,
          permissions,
          companyId: currentUser.companyId,
          isDefault: false,
          createdBy: currentUser.uid
        });
        
        showNotification({
          title: 'Success',
          message: 'Role created successfully',
          type: 'success'
        });
      }
      
      setIsSubmitting(false);
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error('Error saving role:', error);
      setIsSubmitting(false);
      showNotification({
        title: 'Error',
        message: isEditMode 
          ? 'Failed to update role. Please try again.' 
          : 'Failed to create role. Please try again.',
        type: 'error'
      });
    }
  };

  // Array of all menu items (excluding Tenants which is super_admin only)
  const menuItems: { key: keyof RolePermissions; label: string }[] = [
    { key: 'dashboard', label: 'Dashboard' },
    { key: 'invoices', label: 'Invoices' },
    { key: 'customers', label: 'Customers' },
    { key: 'products', label: 'Products' },
    { key: 'transactions', label: 'Transactions' },
    { key: 'users', label: 'Users' },
    { key: 'virtualTerminal', label: 'Virtual Terminal' },
    { key: 'donations', label: 'Webform Payments' },
    { key: 'settings', label: 'Settings' }
  ];

  // Function to check if a menu item is enabled
  const isMenuItemEnabled = (feature: keyof RolePermissions): boolean => {
    if (feature === 'dashboard' || feature === 'settings' || feature === 'virtualTerminal' || feature === 'donations') {
      return !!permissions[feature];
    }
    
    // For feature permissions, check if canView is true
    return !!(permissions[feature] as FeatureAccess).canView;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-[800px] max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="pb-2">
          <DialogTitle>
            {isEditMode 
              ? `Edit ${name} Role` 
              : 'Add Role'}
          </DialogTitle>
          <DialogDescription>
            {isEditMode 
              ? 'Update the role details and menu access permissions.' 
              : 'Create a new role with custom menu access.'}
          </DialogDescription>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="permissions">Menu Access</TabsTrigger>
          </TabsList>
          
          <TabsContent value="general" className="mt-2 flex-1 overflow-auto">
            <div className="space-y-3 px-1">
              <div className="grid grid-cols-4 items-center gap-3">
                <Label htmlFor="name" className="text-right">
                  Role Name
                </Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="col-span-3"
                  placeholder="e.g., Sales Manager"
                />
                {errors.name && <p className="text-destructive text-sm col-start-2 col-span-3">{errors.name}</p>}
              </div>
              
              <div className="grid grid-cols-4 items-start gap-3">
                <Label htmlFor="description" className="text-right pt-2">
                  Description
                </Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="col-span-3 min-h-[100px]"
                  placeholder="Describe the purpose and permissions of this role"
                />
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="permissions" className="mt-2 flex-1 overflow-hidden">
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-4 flex items-start">
              <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5 mr-2 flex-shrink-0" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">Menu-Based Permission System</p>
                <p>This role system controls which menu items a user can access. If a user has access to a menu section, they have full permissions within that section.</p>
              </div>
            </div>
            
            <ScrollArea className="h-[300px] pr-4">
              <div className="space-y-4">
                <Card>
                  <CardContent className="pt-6">
                    {menuItems.map(item => (
                      <div key={item.key} className="flex items-center justify-between py-2 border-t border-gray-100 first:border-0">
                        <div>
                          <h3 className="text-sm font-medium">{item.label}</h3>
                          <p className="text-xs text-gray-500">
                            {isMenuItemEnabled(item.key) 
                              ? "User can access this menu item" 
                              : "User cannot access this menu item"}
                          </p>
                        </div>
                        <Switch
                          checked={isMenuItemEnabled(item.key)}
                          onCheckedChange={(checked) => handleMenuItemAccess(item.key, checked)}
                          disabled={item.key === 'dashboard'} // Dashboard is always enabled
                        />
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>
        
        <DialogFooter className="pt-4 border-t border-gray-100 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="mr-2">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : isEditMode ? 'Update Role' : 'Create Role'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 
